import React from "react";
import { useFormik } from "formik";
import { useHistory } from "react-router-dom";
import * as yup from "yup";
import Alert from "@material-ui/lab/Alert";
import CommuteIcon from "@material-ui/icons/Commute";

import { CloseButton, Panel, PanelHeader } from "../Panel";
import {
  Checkbox,
  FieldWrapper,
  LocalizedSelect,
  LocalizedTextField,
} from "../Input";
import { LocalizedButton } from "../Button";
import { LocalizedText } from "../LocalizedText";
import { SkeletonForm } from "../SkeletonForm";

import {
  VehicleAccessFormData,
  VehicleAccessFormViewProps,
} from "./interfaces";
import { vehicleAccessToFormValues } from "./transformers";
import { useStyles } from "./styles";

/**
 * Validation rules for the form
 */
const schema = yup.object({
  name: yup.string().required("vehicle_access_name_required"),
  vehicles: yup.array().of(yup.string()),
  hasAccessToAllVehicles: yup.boolean().optional(),
});

/**
 * A form for creating or editing a vehicle
 */
export const VehicleAccessFormView: React.FC<VehicleAccessFormViewProps> = ({
  vehicleAccess,
  vehicles,
  loading,
  error,
  success,
  accountId,
  isSubmitting,
  onSubmit,
  isEdit,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const [showSkeleton, setShowSkeleton] = React.useState(isEdit);

  const formik = useFormik<VehicleAccessFormData>({
    initialValues: {
      name: "",
      id: "",
      accountId: "",
      vehicles: [],
      hasAccessToAllVehicles: false,
    },
    validateOnBlur: true,
    validationSchema: schema,
    onSubmit: (values) => {
      onSubmit(values);
    },
  });

  /**
   * Once vehicle has loaded, fill the form
   */
  React.useEffect(() => {
    if (!vehicleAccess) return;

    formik.setValues(vehicleAccessToFormValues(vehicleAccess));

    setShowSkeleton(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formik changes each render so cannot be declared as a dependency
  }, [vehicleAccess]);

  /**
   * Construct header text
   */
  const headerText =
    loading || vehicleAccess ? (
      loading ? (
        <LocalizedText text="loading" />
      ) : (
        <>
          <LocalizedText text="editing" />: {vehicleAccess?.name}
        </>
      )
    ) : (
      <LocalizedText text="create_vehicle_access" />
    );
  const header = (
    <PanelHeader icon={<CommuteIcon />} title={headerText}>
      <CloseButton
        onClick={() =>
          history.push(`/accounts/summary/${accountId}/vehicle/access`)
        }
      />
    </PanelHeader>
  );

  return (
    <Panel header={header}>
      {error && (
        <Alert className={classes.alert} severity="error">
          <LocalizedText text="an_error_occurred" />
        </Alert>
      )}
      {success && (
        <Alert className={classes.alert} severity="info">
          <LocalizedText text="changes_saved" />
        </Alert>
      )}

      {showSkeleton ? (
        <SkeletonForm numFields={4} />
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <FieldWrapper>
            <LocalizedTextField
              fullWidth
              id="name"
              name="name"
              label="vehicle_access_name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </FieldWrapper>

          <FieldWrapper>
            <Checkbox
              checked={formik.values.hasAccessToAllVehicles}
              label="Access For All Vehicles"
              disabled={false}
              onChange={(event) => {
                formik.setFieldValue(
                  "hasAccessToAllVehicles",
                  event.target.checked
                );
              }}
              name="hasAccessToAllVehicles"
            />
          </FieldWrapper>

          <FieldWrapper>
            <LocalizedSelect
              multiple
              fullWidth
              id="vehicles"
              name="vehicles"
              label="vehicles"
              value={formik.values.vehicles}
              onChange={formik.handleChange}
              disabled={!vehicles || formik.values.hasAccessToAllVehicles}
              options={
                vehicles?.map((vehicle) => ({
                  value: vehicle.id,
                  label: vehicle.registration,
                })) || []
              }
            ></LocalizedSelect>
          </FieldWrapper>

          <LocalizedButton fullWidth disabled={isSubmitting} type="submit">
            {isSubmitting ? "saving" : "save"}
          </LocalizedButton>
        </form>
      )}
    </Panel>
  );
};
